import type { TFunction } from 'i18next';
import type { Control } from 'react-hook-form';
import type { AnyZodObject, z } from 'zod';

import type { DefaultBidderConfigDto, IdPlacementDto, PlacementDto } from '@/services/ws-header-tag/src/main/resources/rest-api';

import type { Option } from 'src/types';
import type {
  createFormValuesSchema,
  createPlacementBiddersSchema,
  createPlacementCustomBiddersSchema,
  createAttachPrebidParamsSchema,
  createGoogleBidderConfigSchema,
  createAmazonBidderConfigSchema,
  createAdSenseBidderConfigSchema,
} from './schemas';

export type FormValues = z.infer<
  ReturnType<typeof createFormValuesSchema> & {
    prebidParamsSchema: AnyZodObject;
  }
>;

export type PlacementBidder = z.infer<ReturnType<typeof createPlacementBiddersSchema>>;
export type PlacementCustomBidder = z.infer<ReturnType<typeof createPlacementCustomBiddersSchema>>;

export type GoogleBidderConfig = z.infer<ReturnType<typeof createGoogleBidderConfigSchema>>;
export type AmazonBidderConfig = z.infer<ReturnType<typeof createAmazonBidderConfigSchema>>;
export type AdSenseBidderConfig = z.infer<ReturnType<typeof createAdSenseBidderConfigSchema>>;

export type PlacementBidderConfig = z.infer<ReturnType<typeof createAttachPrebidParamsSchema>>;
export type PlacementCustomBidderConfig = GoogleBidderConfig | AmazonBidderConfig | AdSenseBidderConfig;

export interface DefaultFieldsProps {
  isSubmitting: boolean;
  control: Control<FormValues>;
}

export type BidderZodSchemas = Record<string, AnyZodObject>;

interface AdditionalProperty {
  type: string;
  items?: {
    type: string;
  };
}

export interface Property {
  type: string;
  description?: string;
  required?: string[];
  pattern?: string;
  items?: Property;
  properties?: Record<string, Property>;
  enum?: string[];
}

export type Properties = Record<
  string,
  Property & {
    additionalProperties?: AdditionalProperty;
    minItems?: number;
    maxItems?: number;
  }
>;

export type Schema = {
  properties: Properties;
  required: string[];
  description?: string;
};

export type BidderSchemas = {
  [bidder: string]: Schema | Schema[];
};

export enum PlacementSearchKey {
  CLONE_ID = 'cloneId',
}

export type DynamicPrebidParamFieldName = `placementBidders.${number}.bidderConfigs.${number}.prebidParams.${string}`;

export type KeyValuesFieldName =
  | 'keyValues'
  | `placementBidders.${number}.bidderConfigs.${number}.googleKeyValues`
  | `customBidders.google.bidderConfigs.${number}.keyValues`
  | `customBidders.amazon.bidderConfigs.${number}.keyValues`
  | `customBidders.adSense.bidderConfigs.${number}.keyValues`;

export type CustomField = {
  name: DynamicPrebidParamFieldName;
  control: Control<FormValues>;
  fieldKey: string;
  fieldItem: Property;
  isSubmitting: boolean;
};

export enum CustomBidders {
  GOOGLE = 'google',
  AD_SENSE = 'adSense',
  AMAZON = 'amazon',
}

export interface ConvertPlacementToFormValuesProps {
  t: TFunction;
  placement: PlacementDto;
  bidderSchemas?: BidderSchemas;
  cloned: boolean;
}

export interface ConvertFormValuesToPlacementProps {
  values: FormValues;
  cloned: boolean;
  placementId?: IdPlacementDto;
  comment?: string;
}

export type KeyValue = {
  key: string;
  values: Option<string>[];
};

export type KeyValues = KeyValue[];

export type PrebidParams = Record<string, unknown>;

export type AppendingBidder = {
  bidder: string;
  bidderConfig?: DefaultBidderConfigDto;
};
